import React, { useRef } from 'react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { theme } from '../../styles/theme'
import { Button } from './Button'
import { Download } from 'lucide-react'

export type ChartType = 'line' | 'area' | 'bar' | 'pie'

interface BaseChartProps {
  data: any[]
  type: ChartType
  height?: number
  colors?: string[]
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  showExport?: boolean
  title?: string
}

interface LineAreaChartProps extends BaseChartProps {
  type: 'line' | 'area'
  xKey: string
  yKeys: string[]
}

interface BarChartProps extends BaseChartProps {
  type: 'bar'
  xKey: string
  yKeys: string[]
  stacked?: boolean
}

interface PieChartProps extends BaseChartProps {
  type: 'pie'
  nameKey: string
  valueKey: string
}

type ChartProps = LineAreaChartProps | BarChartProps | PieChartProps

const defaultColors = [
  theme.colors.primary,
  theme.colors.success,
  theme.colors.warning,
  theme.colors.purple,
  theme.colors.info,
]

export const Chart: React.FC<ChartProps> = (props) => {
  const {
    data,
    type,
    height = 300,
    colors = defaultColors,
    showLegend = true,
    showGrid = true,
    showTooltip = true,
    showExport = true,
    title,
  } = props

  const chartRef = useRef<HTMLDivElement>(null)

  const handleExport = () => {
    if (!chartRef.current) return

    // Simple export to data URL (could be enhanced with html2canvas)
    const svg = chartRef.current.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgData], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `chart-${Date.now()}.svg`
    link.click()
    URL.revokeObjectURL(url)
  }

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    }

    switch (type) {
      case 'line': {
        const { xKey, yKeys } = props as LineAreaChartProps
        return (
          <LineChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray200} />}
            <XAxis dataKey={xKey} stroke={theme.colors.textSecondary} />
            <YAxis stroke={theme.colors.textSecondary} />
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
            {yKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ fill: colors[index % colors.length], r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        )
      }

      case 'area': {
        const { xKey, yKeys } = props as LineAreaChartProps
        return (
          <AreaChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray200} />}
            <XAxis dataKey={xKey} stroke={theme.colors.textSecondary} />
            <YAxis stroke={theme.colors.textSecondary} />
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
            {yKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        )
      }

      case 'bar': {
        const { xKey, yKeys, stacked } = props as BarChartProps
        return (
          <BarChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray200} />}
            <XAxis dataKey={xKey} stroke={theme.colors.textSecondary} />
            <YAxis stroke={theme.colors.textSecondary} />
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
            {yKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </BarChart>
        )
      }

      case 'pie': {
        const { nameKey, valueKey } = props as PieChartProps
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={valueKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              outerRadius={height / 3}
              label
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            {showTooltip && <Tooltip />}
            {showLegend && <Legend />}
          </PieChart>
        )
      }
    }
  }

  return (
    <div>
      {(title || showExport) && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing[4],
        }}>
          {title && (
            <h3 style={{
              margin: 0,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.textPrimary,
            }}>
              {title}
            </h3>
          )}
          {showExport && (
            <Button size="sm" variant="ghost" onClick={handleExport} icon={<Download size={16} />}>
              Export
            </Button>
          )}
        </div>
      )}
      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={height}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export const SparklineChart: React.FC<{ data: number[]; color?: string }> = ({
  data,
  color = theme.colors.primary,
}) => {
  const chartData = data.map((value, index) => ({ value, index }))

  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}
